import React, { useEffect, useMemo, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import styled from "styled-components";
import { RouteComponentProps } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import Icon from "@material-ui/core/Icon";
import SelfServiceReport from "../interfaces/SelfServiceReport";
import ContentLayout from "../components/ContentLayout";
import UnstyledButton from "../components/Button";
import { SELF_SERVICE_CREATE_REPORT } from "constants/routes";
import SharedReportList from "./SharedReportList";
import PersonalReportList from "./PersonalReportList";
import { filterReportsByQuery, separateReportsByType } from "./utils";
import { convertIntegerToGUID } from "helpers/guid";
import LoadingScreen from "../components/LoadingScreen";
import {
  deleteSelfServiceReport,
  getSelfServiceReports,
  saveSelfServiceReport,
} from "../service";
import useDataLoader from "hooks/useDataLoader";
import { ToastSuccess } from "components/Global/Toasts/Toasty";

const Title = styled.h2`
  font-family: Inter, system-ui;
  font-size: 22px;
  font-weight: 400;
  line-height: 24px;
  color: #5f6368;
  text-align: center;
  flex: 1;
`;

const SubTitle = styled.h3`
  font-family: Inter, system-ui;
  font-size: 21px;
  font-weight: 400;
  line-height: 24px;
  color: #5f6368;
  margin-right: auto;
`;

const Main = styled.div`
  padding: 30px 40px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const TitleSection = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;
`;

const ReportSection = styled.div`
  margin-bottom: 30px;
  flex: 1;
`;

const Button = styled(UnstyledButton)`
  && {
    margin-left: 20px;
  }
`;

interface ReportsListProps extends RouteComponentProps {
  authentication: { user?: { id: number } };
}

const ReportsList = ({ history, authentication }: ReportsListProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredResult, setFilteredResult] = useState<SelfServiceReport[]>([]);

  const {
    isLoading,
    data: reportList,
    setData: setReportList,
  } = useDataLoader<SelfServiceReport[]>(getSelfServiceReports);

  useEffect(() => {
    setFilteredResult(filterReportsByQuery(reportList ?? [], searchQuery));
  }, [reportList, searchQuery]);

  const ownerId = convertIntegerToGUID(authentication.user?.id ?? 0) ?? "";

  const { personalReports, sharedReports } = useMemo(
    () => separateReportsByType(filteredResult, ownerId),
    [filteredResult, ownerId]
  );

  const editReport = (reportId: string) => {
    history.push(`/reporting/self-service-reports/edit/${reportId}`);
  };

  const duplicateReport = async (oldReport: SelfServiceReport) => {
    const newReport: SelfServiceReport = {
      ...oldReport,
      selfServiceReportId: "00000000-0000-0000-0000-000000000000",
      userId: ownerId,
      name: `Duplicate of ${oldReport.name}`,
    };

    const newReportId = await saveSelfServiceReport(newReport);
    newReport.selfServiceReportId = newReportId;

    const newList = [...(reportList || [])];
    const index = newList.findIndex(
      (r) => r.selfServiceReportId === oldReport.selfServiceReportId
    );
    newList.splice(index + 1, 0, newReport);
    setReportList(newList);

    ToastSuccess("Duplicate successfully created!");
  };

  const deleteReport = async (reportId: string) => {
    await deleteSelfServiceReport(reportId);

    const newList = [...(reportList || [])];
    const index = newList.findIndex((r) => r.selfServiceReportId === reportId);
    newList.splice(index, 1);
    setReportList(newList);

    ToastSuccess("Template successfully deleted!");
  };

  return (
    <ContentLayout centerColumn={<Title>Self Service Reports</Title>}>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <Main>
          <TitleSection>
            <SubTitle>My Templates</SubTitle>
            <SearchBar
              width="300px"
              height="38px"
              placeholder="Search for a template"
              onChange={setSearchQuery}
              value={searchQuery}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(SELF_SERVICE_CREATE_REPORT)}
            >
              <Icon>add</Icon>
              Create new
            </Button>
          </TitleSection>
          <ReportSection>
            <PersonalReportList
              ownerId={ownerId}
              searchQuery={searchQuery}
              reports={personalReports}
              editReport={editReport}
              duplicateReport={duplicateReport}
              deleteReport={deleteReport}
            />
          </ReportSection>
          <TitleSection>
            <SubTitle>Shared With Me</SubTitle>
          </TitleSection>
          <ReportSection>
            <SharedReportList
              searchQuery={searchQuery}
              reports={sharedReports}
              duplicateReport={duplicateReport}
            />
          </ReportSection>
        </Main>
      )}
    </ContentLayout>
  );
};

// @ts-ignore
const mapStateToProps = ({ authentication }) => ({ authentication });

export default compose(connect(mapStateToProps, null))(ReportsList);
